import { Suspense, useEffect, useRef, useState, type ReactElement, type ReactNode } from 'react';
import { lazyWithRetry as lazy } from '@/lib/lazy-with-retry';
import { useLocation, useRoute } from 'wouter';
import { useTranslation } from 'react-i18next';
import { JobsProvider } from '@/lib/v2/jobs';
import { ToastProvider, useToast } from '@/lib/v2/toast';
import type { Job } from '@/lib/v2/jobs';
import ErrorBoundary from '@/components/v2/ErrorBoundary';
import PageTransition from '@/components/v2/PageTransition';
import ResponsiveShell from '@/components/v2/ResponsiveShell';
import Skeleton from '@/components/v2/Skeleton';
import CompanionMount from '@/components/v2/Companion/CompanionMount';
import { useAuth } from '@/contexts/AuthContext';
import { trpc } from '@/lib/trpc';
import { prefetchRoutes } from '@/lib/v2/prefetch-routes';

// Lazy-load v2 pages so they don't bloat the main bundle.
const Landing = lazy(() => import('@/pages/v2/Landing'));
const Auth = lazy(() => import('@/pages/v2/Auth'));
const Pricing = lazy(() => import('@/pages/v2/Pricing'));
const PricingProducts = lazy(() => import('@/pages/v2/PricingProducts'));
const AboutPublic   = lazy(() => import('@/pages/v2/public/About'));
const ContactPublic = lazy(() => import('@/pages/v2/public/Contact'));
const PrivacyPublic = lazy(() => import('@/pages/v2/public/Privacy'));
const TermsPublic   = lazy(() => import('@/pages/v2/public/Terms'));
const RefundPublic  = lazy(() => import('@/pages/v2/public/Refund'));
const Billing = lazy(() => import('@/pages/v2/Billing'));
const CheckoutSuccess = lazy(() => import('@/pages/v2/CheckoutSuccess'));
const CheckoutFailed = lazy(() => import('@/pages/v2/CheckoutFailed'));
const Home = lazy(() => import('@/pages/v2/Home'));
// خطتي — the living Career Plan pillar (ADR-006).
const Plan = lazy(() => import('@/pages/v2/Plan'));
const Posts = lazy(() => import('@/pages/v2/Posts'));
const ContentPreflight = lazy(() => import('@/pages/v2/ContentPreflight'));
const ContentGenerating = lazy(() => import('@/pages/v2/ContentGenerating'));
const ContentEditor = lazy(() => import('@/pages/v2/ContentEditor'));
const Profile = lazy(() => import('@/pages/v2/Profile'));
// /v2/activity retired — its content lives inside /v2/home as the "Recent
// activity" panel. Legacy path now redirects (see matchActivity below).
// V2-wrapped versions of the real V1 feature pages — they reuse the working
// tRPC/business logic but render inside the V2 ProtectedShell chrome.
const CVBuilder = lazy(() => import('@/pages/v2/CVBuilder'));
const CVPreflight = lazy(() => import('@/pages/v2/CVPreflight'));
const CVDiagnostic = lazy(() => import('@/pages/v2/CVDiagnostic'));
const CVBuilding = lazy(() => import('@/pages/v2/CVBuilding'));
const CVEditor = lazy(() => import('@/pages/v2/CVEditor'));
const AdminPanel = lazy(() => import('@/pages/v2/AdminPanel'));
const FinancePanel = lazy(() => import('@/pages/v2/FinancePanel'));
const OpsPanel = lazy(() => import('@/pages/v2/OpsPanel'));
const GrowthPanel = lazy(() => import('@/pages/v2/GrowthPanel'));
const WorkforcePanel = lazy(() => import('@/pages/v2/WorkforcePanel'));
const CustomerSuccessPanel = lazy(() => import('@/pages/v2/CustomerSuccessPanel'));
const RevenueLabPanel = lazy(() => import('@/pages/v2/RevenueLabPanel'));
const ProductIntelPanel = lazy(() => import('@/pages/v2/ProductIntelPanel'));
const CompliancePanel = lazy(() => import('@/pages/v2/CompliancePanel'));
const WarRoomPanel = lazy(() => import('@/pages/v2/WarRoomPanel'));
const SupportInboxPanel = lazy(() => import('@/pages/v2/SupportInboxPanel'));

// 3-stage profile analysis flow: Input → Loading → Result. State bridges
// stages via sessionStorage (see lib/v2/analysisSession.ts). Stage 1 lives
// at /v2/analyze; the V1 single-page page is no longer routed.
const ProfileInput = lazy(() => import('@/pages/v2/analysis/ProfileInput'));
const AnalysisLoading = lazy(() => import('@/pages/v2/analysis/AnalysisLoading'));
const AnalysisResults = lazy(() => import('@/pages/v2/analysis/AnalysisResults'));

// Career Copilot — Sprint 2 surfaces.
const OnboardingWizard = lazy(() => import('@/pages/onboarding/OnboardingWizard'));
const CareerProfileSettings = lazy(() => import('@/pages/v2/CareerProfileSettings'));
const PrivacyAndData = lazy(() => import('@/pages/v2/PrivacyAndData'));

// Career Copilot — Sprint 8 surfaces.
const Notifications         = lazy(() => import('@/pages/v2/Notifications'));
const NotificationSettings  = lazy(() => import('@/pages/v2/NotificationSettings'));

// Smart cover letter — standalone (not the legacy CV bundle add-on).
const CoverLetter = lazy(() => import('@/pages/v2/CoverLetter'));

// Beta program (admin only).
const Beta = lazy(() => import('@/pages/v2/Beta'));

// Promo-code redeem — QR-friendly, user-facing.
const Redeem = lazy(() => import('@/pages/v2/Redeem'));

function V2Loader() {
  // Skeleton-shaped fallback that resembles the page chrome — feels less
  // like a hard interruption than a centered spinner while the chunk loads.
  return (
    <div className="flex min-h-[100dvh] w-full flex-col bg-v2-canvas">
      <div className="h-[52px] border-b border-v2-line" />
      <div className="flex-1 px-[22px] pt-6">
        <Skeleton variant="text" lines={2} className="mb-6" />
        <Skeleton variant="card" className="mb-4" />
        <Skeleton variant="card" />
      </div>
    </div>
  );
}

/**
 * Skip-to-content link rendered at the very top. Hidden until focused.
 */
function SkipLink() {
  const { t } = useTranslation();
  return (
    <a
      href="#v2-main"
      className="absolute start-4 top-2 z-[100] -translate-y-12 rounded-v2-md bg-v2-ink px-3 py-2 font-ar text-[13px] font-semibold text-white shadow-lift transition-transform duration-200 ease-out focus:translate-y-0 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-teal-500/50"
    >
      {t('landing.skipToContent')}
    </a>
  );
}

/**
 * Wraps the protected v2 surface with jobs/toast providers and bridges them:
 * when a job settles, fire a toast.
 *
 * On desktop (≥1024px) ResponsiveShell renders the DesktopShell (sidebar +
 * topbar). On mobile it's a passthrough so each page's own <Phone>+<Topbar>
 * +<BottomNav> chrome continues to work unchanged.
 */
// Routes that an unboarded user is allowed to visit (otherwise we'd risk a
// redirect loop, or hide PDPL controls from someone trying to delete their
// data before onboarding). Anything else under /v2 triggers the redirect.
const ONBOARDING_BYPASS_PREFIXES = [
  '/onboarding',
  '/settings',
  '/billing',
  '/checkout',
  '/me',
  '/war-room',
  '/notifications',
  '/redeem',
];

function shouldBypassOnboardingRedirect(location: string): boolean {
  return ONBOARDING_BYPASS_PREFIXES.some((p) => location === p || location.startsWith(p + '/'));
}

function AuthGate({ children }: { children: ReactNode }) {
  const { user, loading } = useAuth();
  const [location, navigate] = useLocation();
  const [careerProfileChecked, setCareerProfileChecked] = useState(false);
  const checkedForUserRef = useRef<string | null>(null);
  const appOpenPingedForUserRef = useRef<string | null>(null);

  useEffect(() => {
    if (!loading && !user) {
      navigate('/login', { replace: true });
    }
  }, [loading, user, navigate]);

  // Smart dedup: ping the server once per user/session so the notification
  // engine can skip email-only sends that would arrive while the user is
  // actively in-app (10-min window). Fire-and-forget — failures are silent.
  useEffect(() => {
    if (loading || !user) return;
    if (appOpenPingedForUserRef.current === user.id) return;
    appOpenPingedForUserRef.current = user.id;
    trpc.notifications.markAppOpened().catch(() => { /* silent */ });
    // Warm the other in-app route chunks on idle so navigation is instant
    // (no per-page JS fetch). Pure preload — runs once per signed-in user.
    prefetchRoutes();
  }, [loading, user]);

  // After login, fetch the career_profile once per user/session. If null and
  // we're not already on an onboarding-bypass route, redirect to /v2/onboarding.
  useEffect(() => {
    if (loading || !user) return;
    if (checkedForUserRef.current === user.id) return;
    let cancelled = false;
    (async () => {
      try {
        const result = await trpc.careerProfile.get();
        if (cancelled) return;
        checkedForUserRef.current = user.id;
        setCareerProfileChecked(true);
        if (!result?.profile && !shouldBypassOnboardingRedirect(location)) {
          navigate('/onboarding', { replace: true });
        }
      } catch (e) {
        // Failing to fetch shouldn't strand the user — let them through.
        // The careerProfile router being unavailable (e.g. during Sprint 1
        // before migration apply) is a known transient.
        console.warn('[AuthGate] careerProfile.get failed (non-blocking):', e);
        checkedForUserRef.current = user.id;
        setCareerProfileChecked(true);
      }
    })();
    return () => { cancelled = true; };
  }, [loading, user, location, navigate]);

  if (loading) {
    return <V2Loader />;
  }

  if (!user) return null;

  // Block render until the first career_profile check resolves, so a fresh
  // user doesn't see /v2/home flash before redirecting to onboarding.
  if (!careerProfileChecked) {
    return <V2Loader />;
  }

  return <>{children}</>;
}

function ProtectedShell({ children }: { children: ReactNode }) {
  return (
    <ErrorBoundary>
      <ToastProvider>
        <JobsProviderWithToast>
          <SkipLink />
          <ResponsiveShell withSidebar showAccountCluster showPulse>
            <main id="v2-main" className="min-h-[100dvh] lg:min-h-0">
              <PageTransition>
                <AuthGate>{children}</AuthGate>
              </PageTransition>
            </main>
            {/* Career companion — bubble on every protected page except /v2/home
                (where Home renders the embedded card); welcome + tour fire once
                on the dashboard. Mounted here so it's scoped to the user app and
                never leaks onto public/portal shells. */}
            <CompanionMount />
          </ResponsiveShell>
        </JobsProviderWithToast>
      </ToastProvider>
    </ErrorBoundary>
  );
}

/**
 * Portal shell — auth-gated like ProtectedShell, but renders WITHOUT the
 * user-app sidebar / account cluster / pulse strip. Used by the Marketing
 * and Finance portals so they take over the full viewport with their own
 * PortalLayout chrome.
 */
function PortalShell({ children }: { children: ReactNode }) {
  return (
    <ErrorBoundary>
      <ToastProvider>
        <JobsProviderWithToast>
          <SkipLink />
          <main id="v2-main" className="min-h-[100dvh]">
            <PageTransition>
              <AuthGate>{children}</AuthGate>
            </PageTransition>
          </main>
        </JobsProviderWithToast>
      </ToastProvider>
    </ErrorBoundary>
  );
}

/**
 * Checkout-return shell — for /v2/checkout/success and /v2/checkout/failed.
 *
 * Deliberately NOT auth-gated: Moyasar redirects the browser cross-site back
 * to us, and the Supabase session cookie may not have re-hydrated at that
 * instant. AuthGate would bounce the user to /v2/login and they'd lose their
 * ?id=<txn> — never seeing their payment status or landing in their wallet.
 *
 * The success page verifies the payment via a PUBLIC procedure (the txn id is
 * an unguessable capability), so it works with or without a live session, and
 * routes a logged-in user onward to their wallet. Everything else under /v2
 * keeps its ProtectedShell/AuthGate protection untouched.
 *
 * Still gets ErrorBoundary + Toast + Jobs + PageTransition so the page behaves
 * like the rest of the app; just no sidebar / companion / auth gate.
 */
function CheckoutShell({ children }: { children: ReactNode }) {
  return (
    <ErrorBoundary>
      <ToastProvider>
        <JobsProviderWithToast>
          <SkipLink />
          <main id="v2-main" className="min-h-[100dvh]">
            <PageTransition>{children}</PageTransition>
          </main>
        </JobsProviderWithToast>
      </ToastProvider>
    </ErrorBoundary>
  );
}

/**
 * Public-page shell: no jobs/toast providers, but still gets ErrorBoundary,
 * skip link, and PageTransition. On desktop renders DesktopShell without the
 * sidebar or jobs/account cluster (Landing/Auth/Pricing don't need them) and
 * with public-marketing nav links instead of app nav.
 */
function PublicShell({ children }: { children: ReactNode }) {
  const { t } = useTranslation();
  const publicNavLinks = [
    { id: 'home',    label: t('landing.publicNav.home'),    href: '/' },
    { id: 'pricing', label: t('landing.publicNav.pricing'), href: '/pricing' },
    { id: 'login',   label: t('landing.publicNav.login'),   href: '/login' },
    { id: 'signup',  label: t('landing.publicNav.signup'),  href: '/signup', cta: true },
  ];

  return (
    <ErrorBoundary>
      <SkipLink />
      <ResponsiveShell
        withSidebar={false}
        showAccountCluster={false}
        showPulse={false}
        navLinks={publicNavLinks}
      >
        <main id="v2-main" className="min-h-[100dvh] lg:min-h-0">
          <PageTransition>{children}</PageTransition>
        </main>
      </ResponsiveShell>
    </ErrorBoundary>
  );
}

/**
 * Bridges JobsProvider and ToastProvider — onJobSettled needs useToast(),
 * which only resolves under <ToastProvider>.
 */
function JobsProviderWithToast({ children }: { children: ReactNode }) {
  const { showToast } = useToast();
  return (
    <JobsProvider
      onJobSettled={(job: Job) => {
        if (job.status === 'completed') {
          showToast({
            tone: 'success',
            message: 'اكتملت المهمة',
            description: job.title,
            ...(job.resultUrl
              ? {
                  onAction: () => {
                    window.history.pushState(null, '', job.resultUrl!);
                    window.dispatchEvent(new PopStateEvent('popstate'));
                  },
                  actionLabel: 'عرض النتيجة',
                }
              : {}),
          });
        } else if (job.status === 'failed') {
          showToast({
            tone: 'error',
            message: 'فشلت المهمة',
            description: job.error ?? job.title,
          });
        }
      }}
    >
      {children}
    </JobsProvider>
  );
}

function V2Routes(): ReactElement | null {
  const [matchLanding] = useRoute('/');
  const [matchLogin] = useRoute('/login');
  const [matchSignup] = useRoute('/signup');
  const [matchPricing] = useRoute('/pricing');
  const [matchPricingProducts] = useRoute('/pricing/products');
  const [matchAbout]   = useRoute('/about');
  const [matchContact] = useRoute('/contact');
  const [matchPrivacy] = useRoute('/privacy');
  const [matchTerms]   = useRoute('/terms');
  const [matchRefund]  = useRoute('/refund');
  const [matchBilling] = useRoute('/billing');
  const [matchCheckoutSuccess] = useRoute('/checkout/success');
  const [matchCheckoutFailed] = useRoute('/checkout/failed');
  const [matchHome] = useRoute('/home');
  const [matchPlan] = useRoute('/plan');
  const [matchAnalyze] = useRoute('/analyze');
  const [matchAnalyzeLoading] = useRoute('/analyze/loading');
  const [matchAnalyzeResult] = useRoute('/analyze/result/:id');
  const [matchCvs] = useRoute('/cvs');
  const [matchCvsNew] = useRoute('/cvs/new');
  const [matchCvsDiagnose] = useRoute('/cvs/diagnose');
  const [matchCvsBuilding] = useRoute('/cvs/building');
  const [matchCvsEditor] = useRoute('/cvs/:id');
  const [matchPosts] = useRoute('/posts');
  const [matchPostsNew] = useRoute('/posts/new/:type');
  const [matchPostsGenerating] = useRoute('/posts/generating');
  const [matchPostsEditor] = useRoute('/posts/:id');
  const [matchProfile] = useRoute('/me');
  const [matchActivity] = useRoute('/activity');
  const [matchOnboarding] = useRoute('/onboarding');
  const [matchSettingsCareer] = useRoute('/settings/career');
  const [matchSettingsPrivacy] = useRoute('/settings/privacy');
  const [matchSettingsNotifications] = useRoute('/settings/notifications');
  const [matchNotifications] = useRoute('/notifications');
  const [matchAdmin] = useRoute('/admin');
  const [matchMarketing] = useRoute('/marketing');
  const [matchFinance] = useRoute('/finance');
  const [matchOps] = useRoute('/ops');
  const [matchGrowth] = useRoute('/growth');
  const [matchWorkforce] = useRoute('/workforce');
  const [matchCustomerSuccess] = useRoute('/customer-success');
  const [matchRevenueLab] = useRoute('/revenue-lab');
  const [matchProductIntel] = useRoute('/product-intel');
  const [matchCompliance] = useRoute('/compliance');
  const [matchWarRoom] = useRoute('/war-room');
  const [matchSupportInbox] = useRoute('/support');
  const [matchCoverLetter] = useRoute('/cover-letter');
  const [matchBeta] = useRoute('/beta');
  const [matchRedeem] = useRoute('/redeem');

  if (matchLanding) {
    return <PublicShell><Suspense fallback={<V2Loader />}><Landing /></Suspense></PublicShell>;
  }
  if (matchLogin || matchSignup) {
    return <PublicShell><Suspense fallback={<V2Loader />}><Auth /></Suspense></PublicShell>;
  }
  if (matchPricingProducts) {
    return <PublicShell><Suspense fallback={<V2Loader />}><PricingProducts /></Suspense></PublicShell>;
  }
  if (matchPricing) {
    return <PublicShell><Suspense fallback={<V2Loader />}><Pricing /></Suspense></PublicShell>;
  }
  if (matchAbout) {
    return <PublicShell><Suspense fallback={<V2Loader />}><AboutPublic /></Suspense></PublicShell>;
  }
  if (matchContact) {
    return <PublicShell><Suspense fallback={<V2Loader />}><ContactPublic /></Suspense></PublicShell>;
  }
  if (matchPrivacy) {
    return <PublicShell><Suspense fallback={<V2Loader />}><PrivacyPublic /></Suspense></PublicShell>;
  }
  if (matchTerms) {
    return <PublicShell><Suspense fallback={<V2Loader />}><TermsPublic /></Suspense></PublicShell>;
  }
  if (matchRefund) {
    return <PublicShell><Suspense fallback={<V2Loader />}><RefundPublic /></Suspense></PublicShell>;
  }
  if (matchBilling) {
    return <ProtectedShell><Suspense fallback={<V2Loader />}><Billing /></Suspense></ProtectedShell>;
  }
  // Checkout return URLs from Moyasar's hosted form. NOT auth-gated — the
  // cross-site bounce from Moyasar can land here before the Supabase session
  // re-hydrates; AuthGate would kick the user to /v2/login and drop ?id=<txn>.
  // Verification runs through a PUBLIC procedure (the txn id is the capability),
  // so these pages render and recover the payment with or without a session.
  if (matchCheckoutSuccess) {
    return <CheckoutShell><Suspense fallback={<V2Loader />}><CheckoutSuccess /></Suspense></CheckoutShell>;
  }
  if (matchCheckoutFailed) {
    return <CheckoutShell><Suspense fallback={<V2Loader />}><CheckoutFailed /></Suspense></CheckoutShell>;
  }
  if (matchOnboarding) {
    return <ProtectedShell><Suspense fallback={<V2Loader />}><OnboardingWizard /></Suspense></ProtectedShell>;
  }
  if (matchSettingsCareer) {
    return <ProtectedShell><Suspense fallback={<V2Loader />}><CareerProfileSettings /></Suspense></ProtectedShell>;
  }
  if (matchSettingsPrivacy) {
    return <ProtectedShell><Suspense fallback={<V2Loader />}><PrivacyAndData /></Suspense></ProtectedShell>;
  }
  if (matchSettingsNotifications) {
    return <ProtectedShell><Suspense fallback={<V2Loader />}><NotificationSettings /></Suspense></ProtectedShell>;
  }
  if (matchNotifications) {
    return <ProtectedShell><Suspense fallback={<V2Loader />}><Notifications /></Suspense></ProtectedShell>;
  }
  if (matchHome) {
    return <ProtectedShell><Suspense fallback={<V2Loader />}><Home /></Suspense></ProtectedShell>;
  }
  // خطتي — the living Career Plan pillar (ADR-006).
  if (matchPlan) {
    return <ProtectedShell><Suspense fallback={<V2Loader />}><Plan /></Suspense></ProtectedShell>;
  }
  // 3-stage analysis flow — each route renders a dedicated V2 page so the
  // user gets a focused screen per stage instead of the V1 single-page mash.
  if (matchAnalyze) {
    return <ProtectedShell><Suspense fallback={<V2Loader />}><ProfileInput /></Suspense></ProtectedShell>;
  }
  if (matchAnalyzeLoading) {
    return <ProtectedShell><Suspense fallback={<V2Loader />}><AnalysisLoading /></Suspense></ProtectedShell>;
  }
  if (matchAnalyzeResult) {
    return <ProtectedShell><Suspense fallback={<V2Loader />}><AnalysisResults /></Suspense></ProtectedShell>;
  }
  // Sprint 4 — Resume v2: list → preflight → building → editor.
  // Order matters: /v2/cvs/new must match BEFORE /v2/cvs/:id so the slug
  // 'new' isn't treated as a version id.
  if (matchCvsNew) {
    return <ProtectedShell><Suspense fallback={<V2Loader />}><CVPreflight /></Suspense></ProtectedShell>;
  }
  if (matchCvsDiagnose) {
    return <ProtectedShell><Suspense fallback={<V2Loader />}><CVDiagnostic /></Suspense></ProtectedShell>;
  }
  if (matchCvsBuilding) {
    return <ProtectedShell><Suspense fallback={<V2Loader />}><CVBuilding /></Suspense></ProtectedShell>;
  }
  if (matchCvsEditor) {
    return <ProtectedShell><Suspense fallback={<V2Loader />}><CVEditor /></Suspense></ProtectedShell>;
  }
  if (matchCvs) {
    return <ProtectedShell><Suspense fallback={<V2Loader />}><CVBuilder /></Suspense></ProtectedShell>;
  }
  // Smart cover letter — standalone, 49 tokens.
  if (matchCoverLetter) {
    return <ProtectedShell><Suspense fallback={<V2Loader />}><CoverLetter /></Suspense></ProtectedShell>;
  }
  // Sprint 5 — Content v2: hub → preflight → generating → editor.
  // Order matters: more-specific routes match before /v2/posts/:id catches them.
  if (matchPostsNew) {
    return <ProtectedShell><Suspense fallback={<V2Loader />}><ContentPreflight /></Suspense></ProtectedShell>;
  }
  if (matchPostsGenerating) {
    return <ProtectedShell><Suspense fallback={<V2Loader />}><ContentGenerating /></Suspense></ProtectedShell>;
  }
  if (matchPosts) {
    return <ProtectedShell><Suspense fallback={<V2Loader />}><Posts /></Suspense></ProtectedShell>;
  }
  if (matchPostsEditor) {
    return <ProtectedShell><Suspense fallback={<V2Loader />}><ContentEditor /></Suspense></ProtectedShell>;
  }
  if (matchProfile) {
    return <ProtectedShell><Suspense fallback={<V2Loader />}><Profile /></Suspense></ProtectedShell>;
  }
  if (matchActivity) {
    // Activity has been folded into the Home dashboard. Bookmarks redirect.
    window.history.replaceState({}, '', '/home');
    window.dispatchEvent(new PopStateEvent('popstate'));
    return null;
  }
  // /v2/admin is the legacy path — redirect to /v2/marketing so old bookmarks
  // still work but the canonical URL reflects the persona.
  if (matchAdmin) {
    window.history.replaceState({}, '', '/marketing');
    window.dispatchEvent(new PopStateEvent('popstate'));
    return null;
  }
  // Marketing portal — uses its own PortalLayout, not the ProtectedShell.
  // We still need the auth guard from ProtectedShell's logic, so we let the
  // AdminPanel component itself wrap the dashboard in PortalLayout and
  // perform the admin gate.
  if (matchMarketing) {
    return <PortalShell><Suspense fallback={<V2Loader />}><AdminPanel /></Suspense></PortalShell>;
  }
  if (matchFinance) {
    return <PortalShell><Suspense fallback={<V2Loader />}><FinancePanel /></Suspense></PortalShell>;
  }
  if (matchOps) {
    return <PortalShell><Suspense fallback={<V2Loader />}><OpsPanel /></Suspense></PortalShell>;
  }
  if (matchGrowth) {
    return <PortalShell><Suspense fallback={<V2Loader />}><GrowthPanel /></Suspense></PortalShell>;
  }
  if (matchWorkforce) {
    return <PortalShell><Suspense fallback={<V2Loader />}><WorkforcePanel /></Suspense></PortalShell>;
  }
  if (matchCustomerSuccess) {
    return <PortalShell><Suspense fallback={<V2Loader />}><CustomerSuccessPanel /></Suspense></PortalShell>;
  }
  if (matchRevenueLab) {
    return <PortalShell><Suspense fallback={<V2Loader />}><RevenueLabPanel /></Suspense></PortalShell>;
  }
  if (matchProductIntel) {
    return <PortalShell><Suspense fallback={<V2Loader />}><ProductIntelPanel /></Suspense></PortalShell>;
  }
  if (matchCompliance) {
    return <PortalShell><Suspense fallback={<V2Loader />}><CompliancePanel /></Suspense></PortalShell>;
  }
  if (matchWarRoom) {
    return <PortalShell><Suspense fallback={<V2Loader />}><WarRoomPanel /></Suspense></PortalShell>;
  }
  if (matchSupportInbox) {
    return <PortalShell><Suspense fallback={<V2Loader />}><SupportInboxPanel /></Suspense></PortalShell>;
  }
  if (matchBeta) {
    return <PortalShell><Suspense fallback={<V2Loader />}><Beta /></Suspense></PortalShell>;
  }
  // Promo-code redeem — auth-gated (redeem needs a user) but on the onboarding
  // bypass list so a QR scan by a fresh beta signup lands here, not onboarding.
  if (matchRedeem) {
    return <ProtectedShell><Suspense fallback={<V2Loader />}><Redeem /></Suspense></ProtectedShell>;
  }

  return null;
}

export default V2Routes;
